import React, { useEffect, useMemo } from "react";
import useViewport from "components/general/useViewPort";
import { useQuery } from "react-query";
import { getResultsRSC } from "data/Querys";
import NotAvailable from "components/common/NotAvailable";
import Loader from "components/general/Loader";
import MirsTable from "components/general/table/Table";
import { ColumnNoc, ColumnName, ColumnRank, ColumnQualification, ColumnExpander, ColumnStartOrder, ColumnBib } from "components/common/TableColumns";
import Label from "components/general/Label";
import LoaderSubComponent from "components/general/LoaderSubComponent";
import { TableExpandedForFinals as TableExpanded } from "components/sports/csl/events/canoe/TableExpanded";
import { Currents } from "components/sports/csl/events/canoe/Currents";
import PageHeader from "components/common/PageHeader";
import ProgressionRule from "components/common/ProgressionRule";
import Status from "components/presentation/Status";
import CaptionTable from "components/general/table/CaptionTable";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAsterisk } from "@fortawesome/free-solid-svg-icons";
import { isLiveStatus } from "helpers/status";
import { isSemis } from "../helper/CslHelpers";
import { getLongPhaseRSC } from "helpers/rsc";
import SportCode from "components/common/SportCode";
import { useUrlFriendlyToParameters } from "helpers/urlFriendly";


function CslFinals() {

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const { mirs_competition, mirs_rsc, mirs_language } = useUrlFriendlyToParameters()

  // Unit data
  const { data, isError, isLoading } = useQuery({
    queryKey: ['finalsUnits', mirs_competition, mirs_rsc, mirs_language, getLongPhaseRSC(mirs_rsc)],
    queryFn: getResultsRSC,
    refetchInterval: 5000,
    staleTime: 5000,
    refetchIntervalInBackground: true,
    notifyOnChangeProps: ['data', 'error'],
    onError: (error) =>
      console.error(`Something went wrong: ${error.message}`),
  });


  if (isError) { return <NotAvailable />; }
  if (isLoading) { return <Loader />; }
  if (!data?.results?.items) { return <NotAvailable /> }


  return (
    <div className="csl-main">
      <ResultFinals data={data} />
    </div>
  );
}

export default CslFinals;

const ResultFinals = ({ data }) => {

  const { breakPoint } = useViewport();
  const { mirs_rsc } = useUrlFriendlyToParameters()
  const semis = isSemis(mirs_rsc);
  const isSmall = breakPoint === "sm" || breakPoint === "xs"


  const resultStatus = data?.results?.status?.code;
  const scheduleStatus = data?.results?.schedule?.status?.code;

  const isOfficial = data?.results?.status?.code?.includes('OFFICIAL');
  const isStartList = data?.results?.status?.code === 'START_LIST'

  const currentCodes = data?.results?.currentExtendedInfos?.filter(cei => cei?.ei_code === 'CURRENT')?.map(current => current?.ei_value);

  const renderRowSubComponent = React.useCallback(({ row }) => {
    const componentExpanded = <TableExpanded row={row?.original} config={data?.config?.extendedConfig} />
    return (
      <LoaderSubComponent component={componentExpanded} />
    )
    // eslint-disable-next-line
  }, [resultStatus]);

  const falsy = isSmall ? true : false;

  const hiddenColumns = () => {
    const bibArr = isOfficial ? ['Bib', 'StartOrder'] : []
    const qualMarkArr = !semis || isStartList ? ['QualMark'] : []
    if (isSmall) {
      return !semis && isStartList ? ['QualMark', 'Rank', 'StartOrder', 'Result', 'Time', 'Pty', 'Diff'] : isStartList ? ['Rank', 'StartOrder', 'Result', 'Time', 'Pty', 'Diff'] : ['Time', 'Pty', 'Diff', ...bibArr]
    }
    return isStartList ? [...qualMarkArr, ...bibArr, 'Rank', 'Result', 'Time', 'Pty', 'Diff'] : [...qualMarkArr, ...bibArr];
  }

  const initialSortBy = () => {
    return isStartList ? [{ id: 'StartOrder', desc: false }] : [{ id: 'Rank', desc: false }]
  }

  const tableColumns = useMemo(() => {
    return (
      [
        ColumnRank({ disableSort: falsy }),
        ColumnStartOrder({ disableSort: falsy }),
        ColumnBib({ disableSort: falsy }),
        ColumnNoc({ disableSort: falsy }),
        ColumnName({ disableSort: falsy }),
        ColumnTime({}),
        ColumnPty({ label: "HeaderPenaltySecondsR" }),
        ColumnResult({ label: (isSmall ? "HeaderTime" : "HeaderTotal") }),
        ColumnDiff({}),
        ColumnQualification({ className: 'text-center fw-bold' }),
        ColumnExpander({})

      ]
    )
  })
  const _captionField = (<CaptionTable
    leftCol={<span className="fw-bold"><Label data={isStartList ? 'HeaderStartList' : 'HeaderResults'} /></span>}
    rightCol={<Status typeToPrint="result" scheduleStatus={scheduleStatus} resultStatus={resultStatus} />} />)


  return <>
    <PageHeader data={data} />
    <div className='mirs-page-container'>
      {isLiveStatus(resultStatus) && <Currents data={data} currents={currentCodes} />}
      {semis && <ProgressionRule key="progress-component" data={data?.config} filter={{ type: ['QUALIFICATION'], code: ['QUAL_RULE'] }} />}
      <MirsTable
        id={`mirs-table-result-${mirs_rsc}`}
        caption={_captionField}
        data={data?.results?.items}
        columns={tableColumns}
        keyRow={"participant-code"}
        initialSortBy={initialSortBy()}
        hiddenColumns={hiddenColumns()}
        renderRowSubComponent={renderRowSubComponent}
        getRowProps={row => {
          const isCurrent = currentCodes?.includes(row?.original?.participant?.code)
          return {
            className: isCurrent ? 'currentIndicator' : '',
          }
        }}

      />
    </div>
  </>

}

function ColumnTime({
  disableSort = true,
  className = 'text-end',
  headerClassName = 'text-end w-10',
  label = 'HeaderTime',
}) {
  return (
    {
      Header: <Label data={label} />,
      className: className,
      headerClassName: headerClassName,
      disableSortBy: disableSort,
      id: 'Time',
      Cell: (props) => {
        return props.cell.row.original?.extendedResults?.find(er => er?.er_code === 'INTERMEDIATE' && er?.er_pos === 'F')?.er_value2
      }
    }
  )
}

function ColumnPty({
  disableSort = true,
  className = 'text-end',
  headerClassName = 'text-end w-10',
  label = 'HeaderPenalty',
}) {
  return (
    {
      Header: <Label data={label} />,
      className: className,
      headerClassName: headerClassName,
      disableSortBy: disableSort,
      id: 'Pty',
      Cell: (props) => {
        return props.cell.row.original?.pty
      }
    }
  )
}

function ColumnDiff({
  disableSort = true,
  className = 'text-end',
  headerClassName = 'text-end w-10',
  label = 'HeaderBehind',
}) {
  return (
    {
      Header: <Label data={label} />,
      className: className,
      headerClassName: headerClassName,
      disableSortBy: disableSort,
      id: 'Diff',
      Cell: (props) => {
        return props.cell.row.original?.diff
      }
    }
  )
}

function ColumnResult({
  disableSort = true,
  className = 'text-end',
  headerClassName = 'text-end w-10',
  label = 'HeaderResult',
  asterisc = (<FontAwesomeIcon className="font-icon" icon={faAsterisk} />),
}) {
  return (
    {
      Header: <Label data={label} />,
      accessor: 'SortOrder',
      className: className,
      headerClassName: headerClassName,
      disableSortBy: disableSort, //habilita poder ordenar
      id: 'Result',
      Cell: (props) => {
        const res = props.cell.row.original;
        const irmObject = res?.resultIrm ? <SportCode discipline="CSL" entity='@IRM' code={res?.resultIrm} abbr={true} /> : <></>
        const revision = res?.unchecked ? asterisc : '';
        return (
          res.resultType === 'IRM'
            ? <>{revision}{irmObject}</>
            : <>{revision}{res?.resultData}</>
        )
      }
    }
  )
}
